import { XOR, PRODUCT_KEY_MAP } from "../util";

export type Cookie = {
    readonly name: string;
    readonly value: string;
};

export type MeOptions = {
    readonly cloudSessionTokenCookie?: Cookie;
};

export type CloudSessionTokenCookieType = {
    readonly cloudSessionTokenCookie?: Cookie;
};

export type AisAvailableProductsOptions = {
    readonly includeInvalidSites?: boolean;
    readonly includeInvalidBitbucketSite?: boolean;
    readonly removeNonAdminSites?: boolean;
} & CloudSessionTokenCookieType;

export type GetAvailableSitesResponse = {
    readonly sites: Site[];
    readonly flags: SiteFlags;
};

export type GetAvailableSitesRequest = {
    readonly orderId: string;
    readonly transactionAccount: string;
};

export type Site = {
    readonly siteName: string;
    readonly cloudId: string;
    readonly products: string[];
    readonly displayName: string;
    readonly url: string;
    readonly edition: string;
    readonly entitlements: {
        ccpEntitlementId: string;
        billingSourceSystem: string;
        productKey: string;
    }[];
    readonly adminAccess: boolean;
};

export type SiteFlags = {
    readonly useLockedInSite: boolean;
};

export type AvailableProductsType = {
    readonly adminAccess: boolean;
    readonly cloudId: string;
    readonly displayName: string;
    readonly url: string;
    readonly availableProducts: [
        {
            readonly activityCount: number;
            readonly productType: keyof typeof PRODUCT_KEY_MAP;
            readonly url?: string;
        }
    ];
    readonly avatar?: string;
};

export type AisSitesType = {
    readonly adminAccess: boolean;
    readonly cloudId: string;
    readonly products: string[];
    readonly url: string;
    readonly displayName: string;
    readonly edition?: string | null;
    readonly orgId?: string | null;
};
export type AvailableProductsResponse = {
    sites: AisSitesType[];
    isNewUser: boolean;
};

export type AvailableProductsLegacyResponse = SitesType[];

export type AvailableProductsOptions = {
    readonly includeInvalidSites?: boolean;
    readonly includeInvalidBitbucketSite?: boolean;
    readonly removeNonAdminSites?: boolean;
} & CloudSessionTokenCookieType;

export enum AuthenticationMethodsType {
    PASSWORD = "password",
    GOOGLE = "google",
    SAML = "saml",
    MICROSOFT = "microsoft",
    APPLE = "apple",
    SLACK = "slack"
}

export type BitbucketWorkspaceOptions = {
    readonly nextUrl?: string;
    readonly urlQuery?: string;
} & CloudSessionTokenCookieType;

export type BitbucketWorkspacesResponse = {
    readonly page: number;
    readonly pagelen: number;
    readonly size: number;
    readonly next?: string;
    readonly previous?: string;
    readonly values: [
        {
            // owner: has admin access, collaborator: has write access
            readonly permission: "owner" | "member" | "collaborator";
            readonly user?: WorkspaceUserType;
            readonly workspace?: WorkspaceType;
            readonly last_accessed?: string | null;
            readonly type?: string;
            readonly links?: Record<string, unknown>;
            readonly added_on?: string | null;
        }
    ];
};

export type WorkspaceUserType = {
    readonly display_name: string;
    readonly account_id: string;
    readonly links: Record<string, unknown>;
    readonly nickname: string;
    readonly type: string;
    readonly uuid: string;
};

export type WorkspaceType = {
    readonly name: string;
    readonly type: string;
    readonly uuid: string;
    readonly links: Record<string, unknown>;
    readonly slug: string;
};

export type CheckAuthenticationMethodsAvailableRequest = {
    readonly email: string;
    readonly application?: string;
    readonly successRedirect: string;
};
type Method = {
    readonly type: AuthenticationMethodsType;
    readonly redirect_to?: string;
};

export type CheckAuthenticationMethodsAvailableResponse = {
    readonly methods: Method[];
    readonly userExists: boolean;
    readonly passwordEnabled: boolean;
    readonly samlRequired: boolean;
};

export type MeResponse = {
    readonly account_id: string;
    readonly name: string;
    readonly email: string;
    readonly picture: string;
    readonly locale: string;
    readonly zoneinfo: string;
};

export type UserProfile = {
    readonly accountId: string;
    readonly name: string;
    readonly email: string;
    readonly picture: string;
    readonly locale: string;
    readonly zoneinfo: string;
};
export type CcpProduct = {
    readonly offeringId: string;
    readonly pricingPlanId: string;
    readonly productKey: string;
    readonly edition: string;
    readonly chargeElement: string;
    readonly pricingType?: string;
};
export type Platform = "ccp" | "hams" | "routing";
export type Consent = {
    readonly site: string | null;
    readonly locale: string | null;
    readonly formUrl: string | null;
    readonly consents: ConsentsObject[];
};
type ConsentsObject = {
    key: string;
    displayedText: unknown;
    granted: boolean;
};

export type ActivateProductRequest = {
    email: string;
    ccpProducts: CcpProduct[];
    siteName?: string;
    timezone: string;
    useCcpPlatform: boolean;
    platform: Platform;
    displayName?: string;
    consent?: Consent;
    signupContext?: string;
    advancedSettings?: {
        [key: string]: number | boolean | string | Record<string, unknown>;
    };
    gRecaptchaResponse?: string;
    testingPurposes?: boolean;
    isNewUser?: boolean;
    migrationId?: string;
} & CloudSessionTokenCookieType;

export type ActivateProductUpstreamResponse = XOR<
    ActivateProductCcpUpstreamResponse,
    ActivateProductCofsResponse
>;
type ActivateProductResponseItems = {
    itemId: string;
    orderItemId: string;
};
export type ActivateProductCcpUpstreamResponse = {
    statusUrl: string;
    orderId: string;
    slug: string;
    items: ActivateProductResponseItems[];
    transactionAccountId: string;
    invoiceGroupId: string;
    metadata: {
        provisionRequestId: string;
    };
    cloudId: string;
    service: string;
};

export type ActivateProductResponse = XOR<
    ActivateProductCcpResponse,
    ActivateProductCofsResponse
>;

export type ActivateProductCcpResponse = {
    orderId: string;
    slug: string;
    transactionAccountId: string;
    invoiceGroupId: string;
    metadata: {
        provisionRequestId: string;
    };
    items: ActivateProductResponseItems[];
    cloudId: string;
    service: string;
};

export type ActivateProductCofsResponse = {
    cloudId: string;
    progressUri: string;
    service: string;
};

export type CheckCloudNameAvailabilityRequest = {
    readonly cloudName: string;
} & CloudSessionTokenCookieType;
export type CloudNameAvailability =
    | "AVAILABLE"
    | "TAKEN"
    | "ERROR"
    | "INVALID"
    | "NOT_AVAILABLE_FOR_TRANSFER";
export type CheckCloudNameAvailabilityResponse = {
    readonly availability: CloudNameAvailability;
};

export type GetRecommendedSiteNameResponse = {
    readonly recommendedName: string;
};

export type GetUserPermissionsIsPermittedRequest = {
    readonly permissionId: GetUserPermissionsIsPermittedRequestPermissionId;
    readonly resourceId: string;
    readonly dontRequirePrincipalInSite?: boolean;
};

export type GetUserPermissionsIsPermittedResponse = {
    readonly permitted: boolean;
};
export enum GetUserPermissionsIsPermittedRequestPermissionId {
    ADD_PRODUCTS = "add-products",
    INVITE_USERS = "invite-users",
    MANAGE = "manage",
    WRITE = "write",
    /**
     * @deprecated ADD_PRODUCT is a legacy permissionId, use ADD_PRODUCTS instead.
     */
    ADD_PRODUCT = "addProduct"
}
export type PostExpandFreeValidationRequest = {
    readonly cloudId: string;
    readonly productKeys: string[];
};

export type PostExpandFreeValidationResponse = {
    readonly isInBreachOfFree: boolean;
};

export type GetProductEditionRequest = {
    readonly cloudId: string;
    readonly productKey: string;
};

export type GetProductEditionResponse = {
    readonly edition: string;
};

export type GetActivationAuthorizationRequest = {
    readonly productKeys: string[];
    readonly productEdition: string;
    readonly cloudId?: string;
    readonly siteName: string;
};

export type GetActivationAuthorizationResponse = {
    readonly redirectUrl: string;
    readonly authorized: boolean;
};

export type GetCcpHamsStatus = {
    readonly cloudId: string;
    readonly offeringKeys: string;
};
export type GetCcpHamsResponse = {
    readonly service: string;
};

export interface GetLicenceInformationRequest {
    cloudId: string;
}
export type GetLicenseInformationResponse = {
    readonly firstActivationDate: number;
    readonly hostname: string;
    readonly products: {
        [key: string]: GetLicenseInformationProduct;
    };
};

export type GetLicenseInformationProduct = {
    readonly ccpEntitlementId?: string;
    readonly ccpTransactionAccountId: string;
    readonly edition: string;
    readonly isSuspended?: boolean;
    readonly state: string;
    readonly relatesFromEntitlements?: GetLicenseInformationRelatedEntitlement[];
};

export type GetLicenseInformationRelatedEntitlement = {
    readonly relationshipType: string;
    readonly entitlementId: string;
    readonly relationshipId: string;
};

export interface ProductActivateRequest {
    readonly transactionAccountId: string;
    readonly entitlementId: string;
    readonly offerings: OfferingsType[];
    readonly cloudId: string;
    readonly siteName?: string;
    readonly advancedSettings?: advancedSettingsType;
}

export interface ProductActivationStatusRequest {
    provisioningRequestId: string;
}

export interface ProductActivationStatusResponse {
    provisionRequestId: string;
    resourcePackages: ResourcePackage[];
}

export interface ResourceActivationError {
    source: string;
    message: string;
}

export interface ResourcePackage {
    entitlementId: string;
    action: {
        type: "ACTIVATE";
        status: "IN_PROGRESS" | "SUCCESS" | "PROVISIONING_FAILURE";
        errorDetails?: ResourceActivationError[];
    };
    resourceAris: string[];
}

export type GetCloudStatusOptions = {
    readonly cloudId: string;
    readonly requestId: string;
};

export type GetCloudStatusResponse = {
    readonly cloudId: string;
    readonly requestId: string;
    readonly completed: boolean;
    readonly failureReason: Readonly<Record<string, string>>;
    readonly requestType: string;
    readonly successful: boolean;
    readonly billingDetails: unknown;
    readonly bestStatuses: Readonly<Record<string, string>>;
};

// For cross flow /product/activate endpoint
export type advancedSettingsType = {
    readonly signupContext?: string;
};
export type OfferingsType = {
    readonly offeringId: string;
    readonly pricingPlanId: string;
    readonly chargeElement: string;
};
export type ProductActivateResponse = {
    readonly orderId: string;
    readonly progressUri: string;
    readonly cloudId: string;
};

export type PostCloudSignUpRequest = {
    payload: FormPayloadType | FormPayloadActivateProducts;
};

type FormPayloadType = {
    adminUsername: string;
    gRecaptchaResponse?: string;
    cloudName: string | null;
    email: string | null;
    timezone: string | null;
    firstName: string | null;
    lastName: string | null;
    adminPassword?: string | null;
    signupContext?: string;
    dataRegion?: string;
    consent: Consent;
} & FormPayloadActivateProducts;

type FormPayloadActivateProducts = {
    activateProducts?: boolean;
    products: ProductPayloadObject[];
    cloudId?: string;
    cloudName: string | null;
    advancedSettings?: {
        [key: string]: number | boolean | string | Record<string, unknown>;
    };
    signupContext?: string;
};

type ProductPayloadObject = {
    readonly accountName?: string | null;
    readonly edition?: string | null;
    readonly product?: string | null;
    readonly productKey?: string | null;
};

export type ProductType = {
    productKey?: string;
    edition?: string;
};

export type UserSignupRequest = {
    email: string;
    locale: string;
    redirectTo: string;
    displayName: string;
    gRecaptchaResponse?: string;
    products?: ProductType[];
};

export type UserSignupResponse = {
    id: string;
    otpRedirect?: string;
};

export interface ProductActivationStatusRequest {
    provisioningRequestId: string;
}

export interface ProductActivationStatusResponse {
    provisionRequestId: string;
    resourcePackages: ResourcePackage[];
}

export interface ResourcePackage {
    entitlementId: string;
    action: {
        type: RecourcePackageActionTypes;
        status: ProductActivationStatus;
        errorDetails?: ResourceActivationError[];
    };
    resourceAris: string[];
}

export type RecourcePackageActionTypes = "ACTIVATE";

export type ProductActivationStatus =
    | "IN_PROGRESS"
    | "SUCCESS"
    | "PROVISIONING_FAILURE";

export interface ResourceActivationError {
    source: string;
    message: string;
}

export type SitesType = {
    readonly adminAccess: boolean;
    readonly cloudId: string;
    readonly products: string[];
    readonly url: string;
    readonly displayName: string;
    readonly edition?: string | null;
    readonly orgId?: string | null;
};
